import { BackgroundTrackId } from "./types";

/**
 * The wire format for a meditation running inside a call.
 *
 * LiveKit multiplexes one data channel, so meditation traffic carries its own
 * topic and never has to be told apart from the whiteboard's.
 *
 * What travels here is *what is playing and where it is up to* — never the
 * audio itself. The recording reaches the patient as a published LiveKit
 * track, the same way a shared tab's sound does, so the two people hear the
 * same thing at the same moment rather than each streaming their own copy and
 * drifting apart. These packets are what puts the patient's screen in the
 * right state around it.
 */
export const MEDITATION_TOPIC = "alchely-meditation";

/** What both sides show while a meditation is up. */
export type MeditationSessionState = {
    meditationId: string;
    title: string;
    /** False for a script the therapist reads aloud themselves. */
    hasAudio: boolean;
    backgroundTrack: BackgroundTrackId;
    playing: boolean;
    /** Seconds into the meditation, as of `atEpochMs`. */
    positionSeconds: number;
    /** When the sender measured `positionSeconds`, so the receiver can catch up. */
    atEpochMs: number;
    /** Total length, for the patient's progress bar. */
    durationSeconds: number;
};

export type MeditationMessage =
    /** A meditation was put up. The patient's screen follows the therapist's. */
    | { kind: "start"; state: MeditationSessionState }
    /** Play/pause, or a seek: the whole state, so a dropped packet self-heals. */
    | { kind: "state"; state: MeditationSessionState }
    /** The meditation is over, or was closed. Back to the faces. */
    | { kind: "stop" }
    /** Someone joined late and needs to know whether one is running. */
    | { kind: "sync-request" };

/**
 * Where a meditation is up to *now*, given a state that arrived a moment ago.
 *
 * A playing meditation has moved on since the packet was sent, and rendering
 * it at the position the packet carried is what makes a patient's progress bar
 * sit visibly behind the audio they are hearing. A paused one is exactly where
 * it was.
 *
 * Both timestamps are the *receiver's* clock — when the packet landed, and
 * now. The sender's `atEpochMs` is deliberately not used for this: the two
 * machines' clocks are not the same clock, and a laptop a minute out would
 * push the bar a minute out with it.
 */
export function positionAt(
    state: MeditationSessionState,
    receivedAtEpochMs: number,
    nowEpochMs: number
): number {
    if (!state.playing) return state.positionSeconds;
    const elapsed = Math.max(0, (nowEpochMs - receivedAtEpochMs) / 1000);
    const position = state.positionSeconds + elapsed;
    if (state.durationSeconds > 0) {
        return Math.min(position, state.durationSeconds);
    }
    return position;
}

/**
 * Whether a participant may drive the meditation for the room.
 *
 * The therapist chooses what is played and when — a patient starting a
 * meditation on the therapist's screen mid-sentence is not a feature. The rule
 * reads the identity the meeting service minted (`therapist-*` for an account
 * holder, `guest-*` for someone on a share link) and both sides apply it: the
 * sender to decide whether to offer the controls, and the receiver to decide
 * whether to obey a packet. Checking only when sending would leave the rule
 * enforced by a hidden button, which anyone with a console can work around.
 */
export function mayControlMeditation(identity: string): boolean {
    return identity.startsWith("therapist-");
}
